import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Users, ClipboardCheck, Star, AlertOctagon, ShieldCheck,
  ArrowLeft, TrendingUp, Building2, Search,
} from "lucide-react";

const gradeColor = (n) => {
  if (n == null) return "text-slate-400";
  if (n >= 80) return "text-emerald-700";
  if (n >= 60) return "text-amber-700";
  return "text-rose-700";
};

export default function MentorStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mentor/students");
      setStudents(data.students || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return students;
    return students.filter((s) => (s.name || "").toLowerCase().includes(term)
      || (s.association_name || "").toLowerCase().includes(term)
      || (s.major || "").toLowerCase().includes(term));
  }, [students, q]);

  const totals = students.reduce((acc, s) => ({
    tasks: acc.tasks + (s.tasks_done || 0),
    open: acc.open + (s.open_complaints || 0),
    resolved: acc.resolved + (s.resolved_complaints || 0),
  }), { tasks: 0, open: 0, resolved: 0 });

  return (
    <DashboardLayout>
      <div data-testid="mentor-students">
        <div className="mb-6">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">لوحة الموجّه</div>
          <h1 className="mt-2 text-2xl font-heading font-black text-slate-900">أداء طلابي مع الجمعيات</h1>
          <p className="mt-2 text-slate-600 max-w-3xl">
            نظرة موحّدة على كل طالب من طلابك: الجمعية التي يتدرب فيها، مهامه، حضوره، تقييمات الجمعية والموجّه، والشكاوى المرتبطة به.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard icon={Users} label="طلابي" value={new Set(students.map((student) => student.id)).size} tint="bg-teal-50 text-teal-700 border-teal-100" />
          <StatCard icon={ClipboardCheck} label="مهام مكتملة" value={totals.tasks} tint="bg-emerald-50 text-emerald-700 border-emerald-100" />
          <StatCard icon={AlertOctagon} label="شكاوى مفتوحة" value={totals.open} tint="bg-rose-50 text-rose-700 border-rose-100" />
          <StatCard icon={ShieldCheck} label="شكاوى مُغلقة" value={totals.resolved} tint="bg-slate-50 text-slate-700 border-slate-200" />
        </div>

        <div className="mb-5 p-3 bg-white rounded-2xl border border-cream-200 card-shadow flex items-center gap-2">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="بحث بالاسم، الجمعية، أو التخصص"
            className="flex-1 bg-transparent focus:outline-none text-sm"
            data-testid="mentor-search"
          />
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-5 rounded-2xl border border-dashed border-slate-300 bg-white text-center text-slate-500">
            {students.length === 0 ? "لا يوجد طلاب مُسنَدون إليك بعد." : "لا نتائج مطابقة لبحثك."}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="mentor-students-grid">
            {filtered.map((s) => (<StudentCard key={s.application_id || s.id} s={s} />))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <div className={`p-4 rounded-2xl border ${tint} flex items-center gap-3`}>
      <div className="h-10 w-10 rounded-xl bg-white grid place-items-center shadow-sm">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-widest opacity-80">{label}</div>
        <div className="text-2xl font-heading font-black arabic-numerals">{value}</div>
      </div>
    </div>
  );
}

const STATUS_BADGE = {
  pending: { label: "بانتظار اعتماد الأدمن", tint: "bg-slate-100 text-slate-700 border-slate-200" },
  under_review: { label: "قيد المراجعة", tint: "bg-amber-50 text-amber-800 border-amber-200" },
};

function StudentCard({ s }) {
  const totalTasks = s.tasks_total || 0;
  const doneTasks = s.tasks_done || 0;
  const progress = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;
  const preAssigned = STATUS_BADGE[s.application_status];
  const needState = s.open_complaints > 0 ? ["شكوى أو تدخل مفتوح", "bg-rose-50 text-rose-700"] : s.attendance_pct != null && s.attendance_pct < 70 ? ["منخفض الالتزام", "bg-amber-50 text-amber-800"] : (s.tasks_overdue || 0) > 0 ? ["لديه مهمة متأخرة", "bg-orange-50 text-orange-700"] : (s.association_score || 0) >= 85 ? ["أداء مرتفع", "bg-emerald-50 text-emerald-700"] : ["مستقر", "bg-teal-50 text-teal-700"];
  return (
    <Link
      to={s.application_id ? `/dashboard/mentor/journeys/${s.application_id}` : `/dashboard/mentor/student/${s.id}`}
      className="group block p-4 rounded-2xl bg-white border border-cream-200 card-shadow hover:-translate-y-0.5 hover:shadow-xl transition-all"
      data-testid={"mentor-student-" + (s.application_id || s.id)}
    >
      <div className="flex items-start gap-3">
        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-teal-700 to-teal-900 grid place-items-center text-white font-heading font-black">
          {s.name?.[0] || "؟"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading font-black text-slate-900 truncate">{s.name}</div>
          <div className="text-xs text-slate-500 truncate mt-0.5">{s.major || "—"}</div>
        </div>
        {s.open_complaints > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-[10px] font-bold arabic-numerals" title="شكاوى مفتوحة">
            <AlertOctagon className="h-3 w-3" /> {s.open_complaints}
          </span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <span className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold ${needState[1]}`}>{needState[0]}</span>
        {preAssigned && (
          <span
            className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold ${preAssigned.tint}`}
            data-testid={`mentor-student-status-${s.application_status}`}
            title="لن تُفتح الرحلة إلا بعد اعتماد الأدمن"
          >
            {preAssigned.label}
          </span>
        )}
      </div>
      <div className="mt-3 text-xs text-slate-700 space-y-1">
        <div className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-teal-700" /><span className="truncate">{s.association_name || "غير مُسنَد لجمعية"}</span></div>
        {s.opportunity_title && <div className="text-slate-500 truncate ps-5">{s.opportunity_title}</div>}
      </div>

      <div className="mt-3">
        <div className="flex items-center justify-between text-[11px] mb-1">
          <span className="text-slate-500">إنجاز المهام</span>
          <span className="font-bold text-slate-800 arabic-numerals">{doneTasks}/{totalTasks}</span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div className="h-full bg-gradient-to-l from-teal-500 to-teal-700" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center text-[11px]">
        <Mini label="الحضور" value={s.attendance_pct != null ? `${s.attendance_pct}%` : "—"} icon={TrendingUp} tint="text-emerald-700 bg-emerald-50 border-emerald-100" />
        <Mini label="تقييم الجمعية" value={s.association_score ?? "—"} icon={Star} tint={`${gradeColor(s.association_score)} bg-amber-50 border-amber-100`} />
        <Mini label="تقييمك" value={s.mentor_score ?? "—"} icon={Star} tint={`${gradeColor(s.mentor_score)} bg-indigo-50 border-indigo-100`} />
      </div>

      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
        <span className="text-slate-500">اضغط للتفاصيل الكاملة</span>
        <span className="inline-flex items-center gap-1 text-teal-700 font-bold group-hover:gap-2 transition-all">
          <ArrowLeft className="h-3.5 w-3.5" /> فتح
        </span>
      </div>
    </Link>
  );
}

function Mini({ label, value, icon: Icon, tint }) {
  return (
    <div className={`rounded-lg border p-2 ${tint}`}>
      <div className="inline-flex items-center gap-0.5 opacity-80"><Icon className="h-3 w-3" /></div>
      <div className="font-heading font-black text-sm arabic-numerals">{value}</div>
      <div className="opacity-70 text-[10px]">{label}</div>
    </div>
  );
}
